/**
 * Recall vocabularies — the enumerated columns `0010_links_recalls` introduces.
 *
 * Same three-part treatment as every other enum in this schema (ruled 2026-08-04):
 * `text` + `CHECK` in SQL, the constant here, and `constraints.test.ts` asserting the two
 * are set-equal — plus the writability round-trip c13 made mandatory, because two copies
 * of a vocabulary agreeing says nothing about whether the values can be stored.
 *
 * The tables land in c30 because `links.recall_id` points at them; `POST /recalls` is
 * c32. These constants exist now so that commit inherits a vocabulary rather than
 * inventing a second copy of one — which is exactly what c27 found had happened to the
 * five membership roles, written into two `CHECK` constraints and nothing else.
 */
/**
 * What a recall covers. §4: the whole GTIN, or a set of AI 10 lot values.
 *
 * **Ranges are not expressible, and that is a property rather than a gap.** The GS1 grain
 * model has no range semantics and the merge is exact-match only. Do not synthesise a
 * range by enumerating lot strings: real lot schemes are not reliably enumerable and a gap
 * is a miss. A whole-GTIN recall with the affected range stated in the notice text is the
 * correct answer (§3.8).
 */
export const RECALL_SCOPES = ["gtin", "lots"];
/**
 * Where a recall is in its life. §4: active, or lifted.
 *
 * There is no `draft` and no `cancelled`. Publishing a recall is the safety act, and a
 * state that looks published and resolves nothing is the failure §3.8 exists to prevent.
 * Lifting reinstates `recalls.restore` and releases the row lock; the trail survives both.
 */
export const RECALL_STATUSES = ["active", "lifted"];
/**
 * Every transition appended to `recall_events` (§3.8, ruled 2026-08-03, design
 * open-decision #3).
 *
 * Recorded from the first publish, unconditionally, at every tier. **Scope corrections are
 * widen-only** — `scope_widened` has no narrowing counterpart on purpose: narrowing a live
 * recall would silently un-recall units whose packs are already in the field.
 */
export const RECALL_EVENT_KINDS = [
    "published",
    "scope_widened",
    "lifted",
    "restore_reinstated",
];
/**
 * The GPSR-shaped fields §4 sketches for `recalls.notice`, **in the order a notice states
 * them** — c82.
 *
 * §4's column comment, verbatim:
 *
 * > `notice jsonb` — *lang-keyed structured fields, shaped after the EU GPSR recall-notice
 * > template: hazard, affected-product identification, consumer action, contact/remedy.
 * > NEVER auto-insert softening language — GPSR prohibits minimising wording.*
 *
 * ## This is NOT the other three constants in this file, and the difference is the point
 *
 * `RECALL_SCOPES`, `RECALL_STATUSES` and `RECALL_EVENT_KINDS` are **closed vocabularies**:
 * each has a `CHECK` in SQL, a set-equality test against it, and a value outside the set is
 * refused. This one has none of that, deliberately, and `packages/shared`'s own
 * `recallNoticeSchema` argues why at length — the column is validated as *"it is an object"*
 * because **a stricter shape would mean a safety publish refused for a shape reason**, which
 * is the invariant-3 violation the whole recall path is arranged to avoid.
 *
 * So this is an **ordering hint with no authority**. A notice may carry these four, some of
 * them, none of them, or fields nobody has thought of; the renderer puts these first because
 * §4 sketched them in this order, and renders everything else after them rather than dropping
 * it. `apps/pip/src/lib/recall.ts`'s `noticeFields` is the one consumer and states that
 * contract in full.
 *
 * It lives here rather than beside the renderer because the seed names the same four, and a
 * fixture and a renderer disagreeing about a field name is a page that renders a blank
 * notice with every test green. It lives here rather than in `packages/db` because a
 * vocabulary is `packages/shared`'s job and a fixture module is not a place production code
 * should import from.
 *
 * **When it becomes a schema is 3R's ruling**, taken beside the dashboard flow that will
 * author these fields — `recallNoticeSchema`'s closing sentence: *"when the notice page ships
 * (Phase 3) and the fields have consumers, the schema is a ruling, not a route's guess."*
 * c82 gives the fields a consumer and deliberately does not take the ruling.
 */
export const GPSR_NOTICE_FIELDS = [
    "hazard",
    "affectedProducts",
    "consumerAction",
    "contact",
];
/** `screens.md` §11, verbatim. The flow's confirmation and the evidence record both print it. */
export const RECALL_SCOPE_OF_SERVICE = "This publishes a recall notice to people who scan your barcode. It does not notify the " +
    "FDA or other regulators, retailers, distributors, or consumers who don't scan — those " +
    "notifications are your legal obligation and happen separately.";
//# sourceMappingURL=recalls.js.map